import React, { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../utils/api';
import { PageHeader, Badge, Loading, ouvrirDocument } from '../components/Shared';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

const TYPE_DOC_OPTIONS = ['Kbis', 'Assurance RC', 'Habilitation', 'Contrat-cadre', 'Convention', 'Attestation', 'Autre'];

// ── Modal nouveau OF ────────────────────────────────────────────
function ModalNouvelOf({ onSave, onClose }) {
  const [form, setForm] = useState({ nom: '', siret: '', num_declaration: '', email: '', tel: '', adresse1: '', cp: '', ville: '', contact_nom: '', contact_prenom: '', contact_email: '', contact_tel: '', assujetti_tva: false, taux_tva: 0.20 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inp = { width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' };
  const fw = { marginBottom: '0.7rem' };

  function field(label, key, type = 'text') {
    return <div style={fw}><label style={lbl}>{label}</label><input type={type} value={form[key] ?? ''} onChange={e => set(key, e.target.value)} style={inp} /></div>;
  }

  async function handleSave() {
    if (!form.nom.trim()) return setError('Le nom est requis');
    setSaving(true); setError('');
    try {
      await apiFetch('/organismes-formation', { method: 'POST', body: JSON.stringify(form) });
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  const section = (label) => <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>{label}</p>;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '600px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)' }}>+ Nouvel organisme de formation</h3>
        {section('Identification')}
        {field('Nom de l\'organisme *', 'nom')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.7rem' }}>
          {field('SIRET', 'siret')}
          {field('N° déclaration activité', 'num_declaration')}
        </div>
        {section('Coordonnées')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.7rem' }}>
          {field('Email', 'email', 'email')}
          {field('Téléphone', 'tel', 'tel')}
        </div>
        {field('Adresse', 'adresse1')}
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0 0.7rem' }}>
          {field('CP', 'cp')}
          {field('Ville', 'ville')}
        </div>
        {section('Contact principal')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.7rem' }}>
          {field('Prénom', 'contact_prenom')}
          {field('Nom', 'contact_nom')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.7rem' }}>
          {field('Email contact', 'contact_email', 'email')}
          {field('Tél contact', 'contact_tel', 'tel')}
        </div>
        {section('TVA')}
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', cursor: 'pointer', fontSize: '0.88rem' }}>
          <input type="checkbox" checked={form.assujetti_tva} onChange={e => set('assujetti_tva', e.target.checked)} />
          Assujetti à la TVA
        </label>
        {form.assujetti_tva && (
          <div style={{ ...fw, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={lbl}>Taux TVA (%)</label>
            <input type="number" min="0" max="100" step="0.1" value={Math.round(form.taux_tva * 1000) / 10}
              onChange={e => set('taux_tva', Number(e.target.value) / 100)}
              style={{ ...inp, width: '90px' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>%</span>
          </div>
        )}
        {error && <div style={{ background: 'rgba(239,68,68,0.1)', borderRadius: '6px', padding: '0.5rem 0.8rem', color: '#f87171', fontSize: '0.85rem', marginBottom: '0.8rem' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Enregistrement...' : '✓ Créer l\'organisme'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Liste des OF ────────────────────────────────────────────────
export function OrganismesFormationList() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [modal, setModal] = useState(false);
  const [archives, setArchives] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const { data, loading } = useApi('/organismes-formation', { archives: archives ? 'true' : undefined }, [archives, refresh]);
  const list = data?.data || [];

  if (loading) return <Loading />;

  return (
    <div className="page">
      <PageHeader
        title="Organismes de Formation"
        subtitle={`${list.length} organisme${list.length !== 1 ? 's' : ''}`}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button className="btn-secondary" onClick={() => navigate('/formateurs')} style={{ fontSize: '0.85rem' }}>
              ← Formateurs
            </button>
            {hasRole('utilisateur') && (
              <button className="btn-primary" onClick={() => setModal(true)}>+ Nouvel organisme</button>
            )}
          </div>
        }
      />

      <div className="toolbar">
        <div style={{ flex: 1 }} />
        <div className="toolbar-filters">
          <button className={`filter-chip ${archives ? 'active' : ''}`}
            onClick={() => setArchives(a => !a)}
            style={archives ? { borderColor: '#f87171', background: 'rgba(239,68,68,0.15)', color: '#f87171' } : {}}>
            🗄 Archivés
          </button>
        </div>
      </div>

      {list.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg-elevated)', borderRadius: '10px', fontSize: '0.9rem', marginTop: '1rem' }}>
          Aucun organisme de formation enregistré.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: '1rem' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Organisme</th>
                <th>Ville</th>
                <th>Contact</th>
                <th style={{ textAlign: 'center' }}>Formateurs</th>
                <th style={{ textAlign: 'center' }}>Formations</th>
                <th style={{ textAlign: 'center' }}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {list.map(of => (
                <tr key={of.id} style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/organismes-formation/${of.id}`)}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{of.nom}</div>
                    {of.siret && <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>SIRET {of.siret}</div>}
                  </td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    {of.ville ? `${of.cp || ''} ${of.ville}`.trim() : '—'}
                  </td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem' }}>
                    {of.contact_prenom || of.contact_nom ? `${of.contact_prenom || ''} ${of.contact_nom || ''}`.trim() : '—'}
                    {of.contact_email && <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>{of.contact_email}</div>}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{of.nb_formateurs ?? 0}</td>
                  <td style={{ textAlign: 'center', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{of.nb_formations ?? 0}</td>
                  <td style={{ textAlign: 'center' }}>
                    <Badge variant={of.actif ? 'badge-success' : 'badge-danger'}>{of.actif ? 'Actif' : 'Inactif'}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ModalNouvelOf
          onSave={() => { setModal(false); setRefresh(r => r + 1); }}
          onClose={() => setModal(false)}
        />
      )}
    </div>
  );
}

// ── Détail d'un OF ───────────────────────────────────────────────
export default function OrganismeFormationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [refresh, setRefresh] = useState(0);
  const [tab, setTab] = useState('infos');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docForm, setDocForm] = useState({ type_document: 'Autre', date_fin_validite: '', notes: '' });
  const [docFile, setDocFile] = useState(null);
  const [addFormModal, setAddFormModal] = useState(false);
  const [ofFormForm, setOfFormForm] = useState({ formation_id: '', intitule: '', duree_heures: '', description: '', formateur_id: '' });
  const [savingForm, setSavingForm] = useState(false);

  const { data: of, loading } = useApi(`/organismes-formation/${id}`, {}, [refresh]);
  const { data: formationsCat } = useApi('/formations', {}, []);
  const formations_catalogue = formationsCat?.data || [];

  const reload = useCallback(() => setRefresh(r => r + 1), []);

  function startEdit() {
    setForm({
      nom: of.nom || '', siret: of.siret || '', num_declaration: of.num_declaration || '',
      adresse1: of.adresse1 || '', adresse2: of.adresse2 || '', cp: of.cp || '', ville: of.ville || '',
      tel: of.tel || '', email: of.email || '', site_internet: of.site_internet || '',
      contact_nom: of.contact_nom || '', contact_prenom: of.contact_prenom || '',
      contact_email: of.contact_email || '', contact_tel: of.contact_tel || '',
      notes: of.notes || '', actif: of.actif,
      assujetti_tva: of.assujetti_tva || false, taux_tva: parseFloat(of.taux_tva) || 0.20,
    });
    setEditing(true);
  }

  async function saveEdit() {
    setSaving(true);
    try {
      await apiFetch(`/organismes-formation/${id}`, { method: 'PUT', body: JSON.stringify(form) });
      setEditing(false); reload();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  }

  async function uploadDoc() {
    if (!docFile) return alert('Sélectionnez un fichier');
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('fichier', docFile);
      fd.append('type_document', docForm.type_document);
      fd.append('date_fin_validite', docForm.date_fin_validite);
      fd.append('notes', docForm.notes);
      const token = localStorage.getItem('gf_token');
      await fetch(`${API_BASE}/organismes-formation/${id}/documents`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      setDocFile(null); setDocForm({ type_document: 'Autre', date_fin_validite: '', notes: '' });
      reload();
    } catch (e) { alert(e.message); }
    finally { setUploadingDoc(false); }
  }

  async function deleteDoc(docId) {
    if (!window.confirm('Supprimer ce document ?')) return;
    try { await apiFetch(`/of-documents/${docId}`, { method: 'DELETE' }); reload(); }
    catch (e) { alert(e.message); }
  }

  async function addFormation() {
    if (!ofFormForm.formation_id && !ofFormForm.intitule.trim()) return alert('Choisissez une formation du catalogue ou saisissez un intitulé');
    setSavingForm(true);
    try {
      await apiFetch(`/organismes-formation/${id}/formations`, { method: 'POST', body: JSON.stringify(ofFormForm) });
      setAddFormModal(false); setOfFormForm({ formation_id: '', intitule: '', duree_heures: '', description: '', formateur_id: '' }); reload();
    } catch (e) { alert(e.message); }
    finally { setSavingForm(false); }
  }

  async function deleteFormation(fid) {
    if (!window.confirm('Supprimer cette formation du catalogue OF ?')) return;
    try { await apiFetch(`/of-formations/${fid}`, { method: 'DELETE' }); reload(); }
    catch (e) { alert(e.message); }
  }

  if (loading) return <Loading />;
  if (!of) return null;

  const inp = { width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' };
  const fw = { marginBottom: '0.7rem' };
  const val = (label, v) => (
    <div style={{ marginBottom: '0.6rem' }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.1rem' }}>{label}</div>
      <div style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>{v || <span style={{ color: 'var(--text-tertiary)' }}>—</span>}</div>
    </div>
  );

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const TABS = [
    { id: 'infos', label: 'Informations' },
    { id: 'formateurs', label: `Formateurs (${(of.formateurs || []).length})` },
    { id: 'formations', label: `Formations (${(of.formations || []).length})` },
    { id: 'documents', label: `Documents (${(of.documents || []).length})` },
  ];

  return (
    <div className="page">
      <PageHeader
        title={of.nom}
        subtitle={of.ville ? `${of.cp || ''} ${of.ville}`.trim() : 'Organisme de Formation'}
        back={{ label: 'Organismes de Formation', path: '/organismes-formation' }}
        actions={hasRole('utilisateur') && !editing && (
          <button className="btn-secondary" onClick={startEdit}>✏ Modifier</button>
        )}
      />

      {/* Onglets */}
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid var(--border)', marginBottom: '1.5rem' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: '0.55rem 1.1rem', background: 'none', border: 'none', borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent', color: tab === t.id ? 'var(--accent)' : 'var(--text-secondary)', fontWeight: tab === t.id ? 700 : 400, cursor: 'pointer', fontSize: '0.88rem', transition: 'color 0.15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Onglet Informations ── */}
      {tab === 'infos' && (
        editing ? (
          <div className="card" style={{ padding: '1.5rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
              <div><label style={lbl}>Nom *</label><input value={form.nom} onChange={e => setF('nom', e.target.value)} style={inp} /></div>
              <div><label style={lbl}>SIRET</label><input value={form.siret} onChange={e => setF('siret', e.target.value)} style={inp} /></div>
            </div>
            <div style={fw}><label style={lbl}>N° déclaration d'activité</label><input value={form.num_declaration} onChange={e => setF('num_declaration', e.target.value)} style={inp} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
              <div style={fw}><label style={lbl}>Email</label><input type="email" value={form.email} onChange={e => setF('email', e.target.value)} style={inp} /></div>
              <div style={fw}><label style={lbl}>Téléphone</label><input type="tel" value={form.tel} onChange={e => setF('tel', e.target.value)} style={inp} /></div>
            </div>
            <div style={fw}><label style={lbl}>Adresse</label><input value={form.adresse1} onChange={e => setF('adresse1', e.target.value)} style={inp} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0 1rem' }}>
              <div style={fw}><label style={lbl}>CP</label><input value={form.cp} onChange={e => setF('cp', e.target.value)} style={inp} /></div>
              <div style={fw}><label style={lbl}>Ville</label><input value={form.ville} onChange={e => setF('ville', e.target.value)} style={inp} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
              <div style={fw}><label style={lbl}>Prénom contact</label><input value={form.contact_prenom} onChange={e => setF('contact_prenom', e.target.value)} style={inp} /></div>
              <div style={fw}><label style={lbl}>Nom contact</label><input value={form.contact_nom} onChange={e => setF('contact_nom', e.target.value)} style={inp} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
              <div style={fw}><label style={lbl}>Email contact</label><input type="email" value={form.contact_email} onChange={e => setF('contact_email', e.target.value)} style={inp} /></div>
              <div style={fw}><label style={lbl}>Tél contact</label><input type="tel" value={form.contact_tel} onChange={e => setF('contact_tel', e.target.value)} style={inp} /></div>
            </div>
            <div style={fw}><label style={lbl}>Notes</label><textarea value={form.notes} onChange={e => setF('notes', e.target.value)} rows={3} style={{ ...inp, resize: 'vertical' }} /></div>
            <div style={{ borderTop: '1px solid var(--border)', marginBottom: '0.8rem', paddingTop: '0.8rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', cursor: 'pointer', fontSize: '0.88rem' }}>
                <input type="checkbox" checked={form.assujetti_tva} onChange={e => setF('assujetti_tva', e.target.checked)} />
                Assujetti à la TVA
              </label>
              {form.assujetti_tva && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <label style={lbl}>Taux TVA</label>
                  <input type="number" min="0" max="100" step="0.1" value={Math.round(form.taux_tva * 1000) / 10}
                    onChange={e => setF('taux_tva', Number(e.target.value) / 100)}
                    style={{ ...inp, width: '90px' }} />
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>%</span>
                </div>
              )}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.actif} onChange={e => setF('actif', e.target.checked)} /> Organisme actif
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setEditing(false)} disabled={saving}>Annuler</button>
              <button className="btn-primary" onClick={saveEdit} disabled={saving}>{saving ? '...' : '✓ Enregistrer'}</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div className="card" style={{ padding: '1.2rem' }}>
              <p style={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-tertiary)', marginBottom: '0.8rem' }}>Identification</p>
              {val('Nom', of.nom)}
              {val('SIRET', of.siret)}
              {val('N° déclaration activité', of.num_declaration)}
              {val('Email', of.email)}
              {val('Téléphone', of.tel)}
              {val('Adresse', [of.adresse1, of.adresse2, of.cp && of.ville ? `${of.cp} ${of.ville}` : of.ville || of.cp].filter(Boolean).join(', '))}
              {val('TVA', of.assujetti_tva ? `Assujetti — ${Math.round(parseFloat(of.taux_tva || 0.20) * 100)}%` : 'Exonéré TVA (art. 261-4-4° CGI)')}
            </div>
            <div className="card" style={{ padding: '1.2rem' }}>
              <p style={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-tertiary)', marginBottom: '0.8rem' }}>Contact principal</p>
              {val('Interlocuteur', [of.contact_prenom, of.contact_nom].filter(Boolean).join(' '))}
              {val('Email', of.contact_email)}
              {val('Téléphone', of.contact_tel)}
              {of.notes && (
                <>
                  <p style={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-tertiary)', margin: '0.8rem 0' }}>Notes</p>
                  <div style={{ fontSize: '0.88rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{of.notes}</div>
                </>
              )}
            </div>
          </div>
        )
      )}

      {/* ── Onglet Formateurs ── */}
      {tab === 'formateurs' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {(of.formateurs || []).length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Aucun formateur rattaché à cet organisme.<br />
              <span style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)' }}>Rattachez un formateur depuis sa fiche (champ "Organisme de formation").</span>
            </div>
          ) : (
            <table className="data-table" style={{ width: '100%' }}>
              <thead><tr><th>Nom</th><th>Email</th><th>Téléphone</th><th style={{ textAlign: 'center' }}>Statut</th></tr></thead>
              <tbody>
                {(of.formateurs || []).map(f => (
                  <tr key={f.formateur_id} style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/formateurs/${f.formateur_id}`)}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td><span style={{ fontWeight: 600 }}>{f.nom_formateur}</span></td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{f.email || '—'}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{f.tel || '—'}</td>
                    <td style={{ textAlign: 'center' }}><Badge variant={f.actif ? 'badge-success' : 'badge-danger'}>{f.actif ? 'Actif' : 'Inactif'}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Onglet Formations ── */}
      {tab === 'formations' && (
        <div>
          {hasRole('utilisateur') && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.8rem' }}>
              <button className="btn-primary" onClick={() => setAddFormModal(true)}>+ Ajouter une formation</button>
            </div>
          )}
          {(of.formations || []).length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg-elevated)', borderRadius: '10px', fontSize: '0.9rem' }}>
              Aucune formation enregistrée pour cet organisme.
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead><tr><th>Formation</th><th>Type</th><th>Durée</th><th>Formateur</th>{hasRole('utilisateur') && <th />}</tr></thead>
                <tbody>
                  {(of.formations || []).map(f => (
                    <tr key={f.id}>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                          {f.formation_catalogue_intitule || f.intitule}
                        </div>
                        {f.description && <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: '0.15rem' }}>{f.description}</div>}
                      </td>
                      <td>
                        {f.formation_id
                          ? <span style={{ fontSize: '0.78rem', background: 'rgba(26,54,104,0.1)', color: '#1a3668', borderRadius: '4px', padding: '0.1rem 0.4rem' }}>Catalogue CEJ</span>
                          : <span style={{ fontSize: '0.78rem', background: 'rgba(139,92,246,0.1)', color: '#7c3aed', borderRadius: '4px', padding: '0.1rem 0.4rem' }}>Hors catalogue</span>
                        }
                      </td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{f.duree_heures ? `${f.duree_heures}h` : '—'}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{f.formateur_nom || '—'}</td>
                      {hasRole('utilisateur') && (
                        <td style={{ textAlign: 'right' }}>
                          <button onClick={() => deleteFormation(f.id)}
                            style={{ border: 'none', background: 'none', color: '#f87171', cursor: 'pointer', fontSize: '0.85rem', padding: '0.2rem 0.4rem' }}>✕</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Onglet Documents ── */}
      {tab === 'documents' && (
        <div>
          {hasRole('utilisateur') && (
            <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.5rem', alignItems: 'end', flexWrap: 'wrap' }}>
                <div>
                  <label style={lbl}>Type de document</label>
                  <select value={docForm.type_document} onChange={e => setDocForm(f => ({ ...f, type_document: e.target.value }))} style={inp}>
                    {TYPE_DOC_OPTIONS.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Date de fin de validité</label>
                  <input type="date" value={docForm.date_fin_validite} onChange={e => setDocForm(f => ({ ...f, date_fin_validite: e.target.value }))} style={inp} />
                </div>
                <div>
                  <label style={lbl}>Fichier</label>
                  <input type="file" onChange={e => setDocFile(e.target.files[0])} style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }} />
                </div>
              </div>
              <div style={{ marginTop: '0.6rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn-primary" onClick={uploadDoc} disabled={uploadingDoc || !docFile}>
                  {uploadingDoc ? 'Envoi...' : '+ Ajouter le document'}
                </button>
              </div>
            </div>
          )}
          {(of.documents || []).length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg-elevated)', borderRadius: '10px', fontSize: '0.9rem' }}>
              Aucun document enregistré.
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead><tr><th>Document</th><th>Type</th><th>Validité</th><th /></tr></thead>
                <tbody>
                  {(of.documents || []).map(doc => (
                    <tr key={doc.id}>
                      <td style={{ fontWeight: 500, fontSize: '0.88rem' }}>{doc.nom_original}</td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{doc.type_document}</td>
                      <td style={{ fontSize: '0.82rem', color: doc.date_fin_validite && new Date(doc.date_fin_validite) < new Date() ? '#f87171' : 'var(--text-secondary)' }}>
                        {doc.date_fin_validite ? new Date(doc.date_fin_validite).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td style={{ textAlign: 'right', display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                        <button onClick={() => ouvrirDocument({ titre: doc.nom_original || 'Document', url: `/of-documents/${doc.id}/download`, nomFichier: doc.nom_original })} style={{ fontSize: '0.82rem', color: 'var(--accent)', cursor: 'pointer', padding: '0.2rem 0.5rem', borderRadius: '4px', border: '1px solid var(--accent)', background: 'transparent' }}>
                          ↓ Ouvrir
                        </button>
                        {hasRole('utilisateur') && (
                          <button onClick={() => deleteDoc(doc.id)}
                            style={{ border: 'none', background: 'none', color: '#f87171', cursor: 'pointer', fontSize: '0.85rem', padding: '0.2rem 0.4rem' }}>✕</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Modal ajout formation OF ── */}
      {addFormModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', width: '520px', maxWidth: '95vw' }}>
            <h3 style={{ marginBottom: '1.2rem', color: 'var(--text-primary)', fontSize: '1rem' }}>Ajouter une formation</h3>
            <div style={fw}>
              <label style={lbl}>Formation du catalogue CEJ (optionnel)</label>
              <select value={ofFormForm.formation_id} onChange={e => setOfFormForm(f => ({ ...f, formation_id: e.target.value, intitule: '' }))} style={inp}>
                <option value="">— Formation hors catalogue —</option>
                {formations_catalogue.map(f => <option key={f.formation_id} value={f.formation_id}>{f.intitule}</option>)}
              </select>
            </div>
            {!ofFormForm.formation_id && (
              <div style={fw}><label style={lbl}>Intitulé (hors catalogue) *</label><input value={ofFormForm.intitule} onChange={e => setOfFormForm(f => ({ ...f, intitule: e.target.value }))} style={inp} /></div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.7rem' }}>
              <div style={fw}><label style={lbl}>Durée (h)</label><input type="number" value={ofFormForm.duree_heures} onChange={e => setOfFormForm(f => ({ ...f, duree_heures: e.target.value }))} style={inp} /></div>
              <div style={fw}>
                <label style={lbl}>Formateur désigné</label>
                <select value={ofFormForm.formateur_id} onChange={e => setOfFormForm(f => ({ ...f, formateur_id: e.target.value }))} style={inp}>
                  <option value="">— Non défini —</option>
                  {(of.formateurs || []).map(f => <option key={f.formateur_id} value={f.formateur_id}>{f.nom_formateur}</option>)}
                </select>
              </div>
            </div>
            <div style={fw}><label style={lbl}>Description</label><textarea value={ofFormForm.description} onChange={e => setOfFormForm(f => ({ ...f, description: e.target.value }))} rows={2} style={{ ...inp, resize: 'vertical' }} /></div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <button className="btn-secondary" onClick={() => setAddFormModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={addFormation} disabled={savingForm}>{savingForm ? '...' : '✓ Ajouter'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
